/**
 * Custom Categories Preparation Utility
 *
 * Prepares custom category data for API submission based on user's category mode selection.
 * Handles predefined, custom, and hybrid category configurations.
 *
 * @module utils/jobSubmission/prepareCustomCategories
 */

import { CategoryMode } from '../../types';

export interface CustomCategoriesData {
  categories: string[];
  fallbackToPredefined: boolean;
  classificationMode: 'color' | 'content' | 'semantic' | 'mixed';
}

export interface CategoryPreparationInput {
  categoryMode: CategoryMode;
  customCategories: string[];
}

/**
 * Prepares custom categories data for API submission
 *
 * @param input - Category configuration from user state
 * @returns Custom categories object for API, or null if using predefined categories
 *
 * @example
 * ```typescript
 * const customCategoriesData = prepareCustomCategories({
 *   categoryMode: 'custom',
 *   customCategories: ['Vacation', 'Work', 'Family']
 * });
 * // Returns: { categories: [...], fallbackToPredefined: false, classificationMode: 'mixed' }
 * ```
 */
export function prepareCustomCategories(
  input: CategoryPreparationInput
): CustomCategoriesData | null {
  // Only prepare custom categories if not using predefined mode
  if (input.categoryMode === 'predefined') {
    return null;
  }

  // Require at least one custom category
  if (input.customCategories.length === 0) {
    return null;
  }

  return {
    categories: input.customCategories,
    fallbackToPredefined: input.categoryMode === 'hybrid',
    classificationMode: 'mixed' as const,
  };
}

/**
 * Validates that custom categories are properly configured
 *
 * @param input - Category configuration to validate
 * @returns True if configuration is valid, false otherwise
 */
export function validateCategoryConfiguration(
  input: CategoryPreparationInput
): boolean {
  // Predefined mode is always valid
  if (input.categoryMode === 'predefined') {
    return true;
  }

  // Custom and hybrid modes require at least one category
  return input.customCategories.length > 0;
}
